// =============================================================================
// Document Management — the chrome the Documents screens share.
//
// Laid out as a file manager rather than a dashboard: a rail that holds the tree, a list that fills the
// rest, and a details pane that slides over it. Someone looking for one document among thousands is
// navigating, not reading, so the list stays dense and the only decoration is what tells rows apart.
// =============================================================================

import React from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { Icon, IconKey } from '/imports/ui/Icon';
import { useFindOne, useUser } from '/imports/hooks';
import { Accounts } from '/imports/api/accounts';
import { DriveAccessLevel, DriveFileRow, DriveFolderRow, driveCanAccess, driveCanManageOrg } from '/imports/api/drive';

export const DrivePageStyled = styled(Page)`
  .page-body { height: 0; }

  .drive {
    display: grid;
    grid-template-columns: 17rem minmax(0, 1fr);
    gap: 1rem;
    align-items: start;
    ${css.mobileMedia} { grid-template-columns: minmax(0, 1fr); }
  }
  .drive-rail {
    border-right: 1px solid ${css.grey7};
    padding-right: 0.75rem;
    min-height: 60vh;
    ${css.mobileMedia} { border-right: none; padding-right: 0; min-height: 0; }
  }
  .drive-rail .rail-link {
    display: flex; align-items: center; gap: 0.4rem; width: 100%;
    background: none; border: none; cursor: pointer; text-align: left;
    padding: 0.3rem 0.4rem; border-radius: ${css.inputBorderRadius}; font-size: 0.85rem; color: ${css.grey1};
  }
  .drive-rail .rail-link:hover { background: ${css.grey9}; }
  .drive-rail .rail-link.active { background: ${css.primaryColorTransparent}; color: ${css.primaryColor}; font-weight: 600; }
  .drive-rail h3 {
    font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: ${css.grey4};
    margin: 1rem 0 0.35rem;
  }

  .drive-bar {
    display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center;
    padding-bottom: 0.6rem; border-bottom: 1px solid ${css.grey7}; margin-bottom: 0.5rem;
  }
  .drive-bar .search { width: 16rem; }
  .drive-bar input { height: 2.2rem; min-height: 2.2rem; font-size: 0.85rem; }
  .drive-bar .spacer { margin-left: auto; }
  .drive-bar .btn { height: 2.2rem; min-height: 2.2rem; font-size: 0.85rem; padding: 0 0.8rem; }

  .crumbs { display: flex; flex-wrap: wrap; align-items: center; gap: 0.2rem; font-size: 0.85rem; margin-bottom: 0.4rem; }
  .crumbs .crumb { background: none; border: none; cursor: pointer; color: ${css.primaryColor}; padding: 0.1rem 0.2rem; }
  .crumbs .crumb:disabled { color: ${css.grey1}; cursor: default; font-weight: 600; }
  .crumbs .sep { color: ${css.grey5}; }

  table.drive-list { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
  table.drive-list th, table.drive-list td { padding: 0.35rem 0.5rem; text-align: left; border-bottom: 1px solid ${css.grey7}; }
  table.drive-list th {
    color: ${css.grey3}; font-weight: 600; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em;
    position: sticky; top: 0; background: ${css.white}; z-index: 1;
  }
  table.drive-list th.num, table.drive-list td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.drive-list td.name { max-width: 0; width: 60%; }
  table.drive-list tbody tr:hover { background: ${css.grey9}; }
  table.drive-list tr { cursor: pointer; }
  table.drive-list .cell-name { display: flex; align-items: center; gap: 0.45rem; min-width: 0; }
  table.drive-list .cell-name .label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cell-name .thumb {
    width: 2rem; height: 2rem; object-fit: cover; border-radius: ${css.inputBorderRadius};
    border: 1px solid ${css.grey7}; background: ${css.grey9}; flex: none;
  }
  table.drive-list .row-tools { width: 1%; white-space: nowrap; text-align: right; }

  .icon-btn {
    background: none; border: none; cursor: pointer; padding: 0.15rem; line-height: 0;
    border-radius: ${css.inputBorderRadius};
  }
  .icon-btn:hover { background: ${css.grey8}; }
  .star.on svg * { fill: #e3a008; }

  table.drive-list .snippet {
    color: ${css.grey4}; font-size: 0.76rem; margin-left: 1.55rem;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .drive-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(10.5rem, 1fr));
    gap: 0.75rem;
    padding: 0.25rem 0 0.5rem;
  }
  .drive-grid .tile {
    border: 1px solid ${css.grey7}; border-radius: ${css.inputBorderRadius};
    overflow: hidden; cursor: pointer; background: ${css.white};
  }
  .drive-grid .tile:hover { border-color: ${css.grey5}; background: ${css.grey9}; }
  .drive-grid .tile:focus-visible { outline: 2px solid ${css.primaryColor}; outline-offset: 1px; }
  .drive-grid .art {
    position: relative; aspect-ratio: 4 / 3; display: flex; align-items: center; justify-content: center;
    background: ${css.grey9}; border-bottom: 1px solid ${css.grey7};
  }
  .drive-grid .art img { width: 100%; height: 100%; object-fit: cover; display: block; }
  /* A row of empty stars is noise on a wall of photographs: the control appears when it has something
     to say, or when the pointer is on the tile that owns it. */
  .drive-grid .art .star {
    position: absolute; top: 0.25rem; right: 0.25rem; background: rgba(255,255,255,0.82);
    opacity: 0; transition: opacity 0.12s;
  }
  .drive-grid .tile:hover .art .star, .drive-grid .tile:focus-within .art .star, .drive-grid .art .star.on { opacity: 1; }
  .drive-grid .art .count {
    position: absolute; bottom: 0.3rem; right: 0.45rem; font-size: 0.72rem; color: ${css.grey3};
    font-variant-numeric: tabular-nums;
  }
  .drive-grid .foot { display: flex; align-items: flex-start; gap: 0.2rem; padding: 0.35rem 0.4rem 0; }
  .drive-grid .foot .label {
    flex: 1; min-width: 0; font-size: 0.78rem; line-height: 1.25; word-break: break-word;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .drive-grid .foot .tools { display: flex; align-items: center; gap: 0.1rem; flex: none; }
  .drive-grid .sub { font-size: 0.7rem; padding: 0 0.4rem 0.4rem; word-break: break-all; }

  .view-toggle { display: flex; gap: 0.15rem; }
  .view-toggle .icon-btn.on { background: ${css.primaryColorTransparent}; }

  .listing-foot {
    display: flex; align-items: center; gap: 0.6rem; padding: 0.6rem 0.25rem; font-size: 0.8rem;
    color: ${css.grey4};
  }

  .upload-caption { font-size: 0.8rem; margin: 0.75rem 0 0.25rem; }
  .muted { color: ${css.grey4}; }
  .empty { padding: 1.5rem 0.5rem; color: ${css.grey4}; }
  .chip {
    display: inline-block; padding: 0.1rem 0.45rem; border-radius: 999px;
    font-size: 0.72rem; font-weight: 600; white-space: nowrap;
  }
  .chip.grey { background: ${css.grey8}; color: ${css.grey2}; }
  .chip.blue { background: ${css.primaryColorTransparent}; color: ${css.primaryColor}; }
`;

/**
 * The chrome a menu carries with it.
 *
 * Both menus in this module — a row's popover and the move dialog's root shortcut — render through a
 * BODY-LEVEL PORTAL, which is outside DrivePageStyled's subtree. Styled on the page they arrived with no
 * styles at all and fell back to default buttons: inline-block, outset border, wrapping two to a row.
 * So the wrapper goes around the items, where the portal can take it with them.
 */
export const DriveMenuStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;

  .menu-item {
    display: flex; align-items: center; gap: 0.55rem; width: 100%;
    background: none; border: none; cursor: pointer; text-align: left;
    padding: 0.45rem 0.75rem; font-size: 0.85rem; line-height: 1.3; color: ${css.grey1};
    white-space: nowrap;
  }
  /* Fixed width rather than the glyph's own, so the labels line up down the menu whatever is beside them. */
  .menu-item .icon { flex: none; width: 1rem; line-height: 0; }
  .menu-item:hover:not(:disabled) { background: ${css.grey9}; }
  .menu-item:disabled { color: ${css.grey5}; cursor: default; }
  .menu-item:disabled svg * { fill: ${css.grey6}; }
  .menu-item.danger { color: ${css.red4}; }
  .menu-sep { height: 1px; background: ${css.grey7}; margin: 0.25rem 0; }
`;

/** What the signed-in account may do in this module, before any per-folder permission is consulted. */
export const useDriveAccess = () => {
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  return {
    user,
    canAccess: driveCanAccess(user, account),
    canManageOrg: driveCanManageOrg(user, account),
    isSuperUser: !!user?.isSuperUser,
  };
};

/** What a row menu was opened on, whichever view opened it. */
export type DriveTarget =
  | { kind: 'folder', row: DriveFolderRow }
  | { kind: 'file', row: DriveFileRow };

/** Label for an access level, as the permissions list and the row chips say it. */
export const driveAccessLabel = (access?: DriveAccessLevel): string => {
  switch (access) {
    case 'manager': return strings.AccessManager();
    case 'editor': return strings.AccessEditor();
    default: return strings.AccessViewer();
  }
};

// Extension → icon. Only the families worth telling apart at a glance; everything else is a plain file,
// because a wall of near-identical glyphs is no faster to read than one repeated glyph.
const extIcons: { [ext: string]: IconKey } = {
  'pdf': 'filePresent',
  'xls': 'table', 'xlsx': 'table', 'csv': 'table', 'ods': 'table',
  'png': 'image', 'jpg': 'image', 'jpeg': 'image', 'gif': 'image', 'webp': 'image', 'heic': 'image', 'tif': 'image', 'tiff': 'image',
};

/** @param ext - the file's extension @returns the icon to show beside its name */
export const driveFileIcon = (ext?: string): IconKey => extIcons[(ext || '').toLowerCase()] || 'docs';

/**
 * A file or folder name with its icon — or, where one has been made, its thumbnail in place of the icon.
 * A folder of site photographs is unreadable as a list of identical glyphs.
 */
export const DriveName = (props: { name: string, ext?: string, folder?: boolean, thumbUrl?: string }) => (
  <span className="cell-name">
    {props.thumbUrl && !props.folder
      ? <img className="thumb" src={props.thumbUrl} alt="" loading="lazy" />
      : <Icon icon={props.folder ? 'folder' : driveFileIcon(props.ext)} size={17} fill={props.folder ? css.primaryColor : css.grey3} />}
    <span className="label">{props.name}</span>
  </span>
);
